/* eslint-disable react/prop-types */
import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { keyframes } from '@emotion/react';
import { CountryCodeEnum } from 'features/common/types';
import { getWhatsAppUrl } from 'constants/whatsappBanners';

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
`;

const pulseButton = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
`;

const shine = keyframes`
  0% {
    transform: translateX(-100%) translateY(-50%);
    opacity: 0;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    transform: translateX(200%) translateY(-50%);
    opacity: 0;
  }
`;

const blink = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.1);
  }
`;

interface Props {
	countryCode: CountryCodeEnum;
}

export const WhatsAppBanner: React.FC<Props> = ({ countryCode }) => {
	const whatsappUrl = getWhatsAppUrl(countryCode);

	// Si no hay URL configurada para este país, no mostrar el banner
	if (!whatsappUrl) {
		return null;
	}

	const handleClick = () => {
		window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
	};

	return (
		<Container
			maxWidth="md"
			sx={{
				marginTop: 2,
				marginBottom: 2,
			}}
		>
			<Box
				onClick={handleClick}
				sx={{
					width: '100%',
					backgroundColor: '#FFFFFF',
					border: '2px solid #31174B',
					borderRadius: '8px',
					padding: { xs: '16px', sm: '20px' },
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					flexWrap: { xs: 'wrap', sm: 'nowrap' },
					gap: { xs: '12px', sm: '16px' },
					cursor: 'pointer',
					transition: 'all 0.2s ease-in-out',
					position: 'relative',
					'&:hover': {
						boxShadow: '0 2px 8px rgba(49, 23, 75, 0.15)',
						transform: 'translateY(-1px)',
					},
				}}
			>
				{/* Badge "🔥 NUEVO" */}
				<Box
					sx={{
						position: 'absolute',
						top: '-8px',
						right: { xs: '8px', sm: '16px' },
						backgroundColor: '#FF4444',
						color: '#FFFFFF',
						padding: '4px 10px',
						borderRadius: '12px',
						fontSize: '0.75rem',
						fontWeight: 700,
						boxShadow: '0 2px 8px rgba(255, 68, 68, 0.4)',
						zIndex: 10,
						pointerEvents: 'none',
					}}
				>
					🔥 NUEVO
				</Box>

				{/* Ícono WhatsApp y texto */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: '12px',
						flex: 1,
						minWidth: 0,
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							flexShrink: 0,
							width: '32px',
							height: '32px',
							position: 'relative',
							'& svg': {
								animation: `${pulse} 2s ease-in-out infinite`,
							},
							// Círculo rojo de notificación parpadeante
							'&::after': {
								content: '""',
								position: 'absolute',
								top: '-2px',
								right: '-2px',
								width: '12px',
								height: '12px',
								backgroundColor: '#FF4444',
								borderRadius: '50%',
								border: '2px solid #FFFFFF',
								animation: `${blink} 1.5s ease-in-out infinite`,
								zIndex: 2,
							},
						}}
					>
						<svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"
								fill="#25D366"
							/>
						</svg>
					</Box>
					<Typography
						variant="body1"
						sx={{
							fontSize: { xs: '0.875rem', sm: '1rem' },
							color: '#31174B',
							fontWeight: 500,
							lineHeight: 1.4,
							flex: 1,
						}}
					>
						Sumate al canal y recibí promos EXCLUSIVAS en paquetes y vuelos ✈️🔥
					</Typography>
				</Box>

				{/* Botón "Unirme" con pulso más marcado */}
				<Button
					variant="contained"
					onClick={(e) => {
						e.stopPropagation();
						handleClick();
					}}
					sx={{
						backgroundColor: '#F46704',
						color: '#FFFFFF',
						borderRadius: '6px',
						padding: { xs: '8px 16px', sm: '10px 20px' },
						fontSize: { xs: '0.875rem', sm: '0.9375rem' },
						fontWeight: 700,
						textTransform: 'none',
						whiteSpace: 'nowrap',
						flexShrink: 0,
						position: 'relative',
						overflow: 'hidden',
						animation: `${pulseButton} 2s ease-in-out infinite`,
						boxShadow: '0 4px 12px rgba(244, 103, 4, 0.3)',
						'&::before': {
							content: '""',
							position: 'absolute',
							top: '50%',
							left: '0',
							width: '50%',
							height: '100%',
							background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
							transform: 'translateY(-50%)',
							animation: `${shine} 3s ease-in-out infinite`,
							zIndex: 1,
							pointerEvents: 'none',
						},
						'&:hover': {
							backgroundColor: '#E05A03',
							boxShadow: '0 6px 16px rgba(244, 103, 4, 0.4)',
						},
					}}
				>
					<span style={{ position: 'relative', zIndex: 2 }}>Unirme</span>
				</Button>
			</Box>
		</Container>
	);
};
